/**
 * First-party commercial attribution (Phase 2 · consent-mode capture).
 *
 * Captures an anonymous visitor id + UTM/referrer/landing "touches" and sends
 * commercially meaningful events to our own backend (/api/track) so the Growth
 * Command Centre can attribute revenue to acquisition. First-party only.
 *
 * Privacy (consent-mode model, mirrors Google Consent Mode):
 *  - Explicit "rejected" or browser Do-Not-Track → we send NOTHING.
 *  - "accepted" → persistent anonymous id in localStorage; full cross-session
 *    journey stitching.
 *  - Not yet chosen / "essential" → COOKIELESS, NON-PERSISTENT, fully anonymous
 *    pings only: an in-memory id regenerated each page load, NO device storage
 *    read/written, NO PII. Lets us count channels/landing pages/funnel in
 *    aggregate under legitimate interest without tracking an individual.
 * Never stores the IP or any PII.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const ANON_KEY = 'cpdf_anon';
const ATTR_KEY = 'cpdf_attr';      // { first_touch, latest_touch } (consented only)
const SESSION_KEY = 'cpdf_sid';

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID()
  : 'xxxxxxxxyxxxx'.replace(/[xy]/g, c => ((Math.random() * 16) | 0).toString(16)));

// In-memory (non-persistent) state for the unconsented "cookieless" path.
const _ephemeralAnonId = `eph-${uuid()}`;
let _memTouch = {};

function dntOn() {
  try { return navigator.doNotTrack === '1' || window.doNotTrack === '1'; } catch { return false; }
}

/** 'accepted' | 'rejected' | 'pending' */
function consentState() {
  try {
    const c = localStorage.getItem('cookie_consent');
    if (c === 'accepted') return 'accepted';
    if (c === 'rejected') return 'rejected';
    return 'pending';
  } catch { return 'pending'; }
}

/** May we send anything at all? (false only on explicit reject / DNT) */
function mayTrack() {
  if (dntOn()) return false;
  return consentState() !== 'rejected';
}

const consented = () => consentState() === 'accepted';

export function getAnonId() {
  if (!consented()) return _ephemeralAnonId;  // cookieless: never touch storage
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) { id = uuid(); localStorage.setItem(ANON_KEY, id); }
    return id;
  } catch { return _ephemeralAnonId; }
}

function getSessionId() {
  if (!consented()) return _ephemeralAnonId;
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uuid(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return uuid(); }
}

function parseUTM() {
  try {
    const q = new URLSearchParams(window.location.search);
    const utm = {};
    ['source', 'medium', 'campaign', 'content', 'term'].forEach(k => {
      const v = q.get(`utm_${k}`); if (v) utm[k] = v;
    });
    // Google Ads auto-tagging fallback: gclid implies paid google/cpc.
    if (!utm.source && q.get('gclid')) { utm.source = 'google'; utm.medium = 'cpc'; }
    // Referrer-based social inference (LinkedIn etc.) when posts aren't UTM-tagged.
    if (!utm.source) {
      const ref = (document.referrer || '').toLowerCase();
      const socials = { linkedin: ['linkedin.', 'lnkd.in'], facebook: ['facebook.', 'fb.com'],
        twitter: ['twitter.', 'x.com', 't.co'], youtube: ['youtube.', 'youtu.be'],
        instagram: ['instagram.'], reddit: ['reddit.'] };
      for (const [net, domains] of Object.entries(socials)) {
        if (domains.some(d => ref.includes(d))) { utm.source = net; utm.medium = 'social'; break; }
      }
    }
    return utm;
  } catch { return {}; }
}

function readStored() {
  if (!consented()) return _memTouch;  // memory-only when not consented
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || '{}'); } catch { return {}; }
}

/** Capture the current touch — set first_touch once, refresh latest_touch when meaningful. */
export function captureTouch() {
  const utm = parseUTM();
  const ref = document.referrer || '';
  const externalRef = ref && !ref.includes('cpdforgeai.com') && !ref.includes(window.location.host);
  const meaningful = Object.keys(utm).length > 0 || externalRef;
  const touch = {
    utm, referrer: ref || null, landing_page: window.location.pathname,
    at: new Date().toISOString(),
  };
  const stored = readStored();
  const next = { ...stored };
  if (meaningful || !stored.first_touch) {
    if (!stored.first_touch) next.first_touch = touch;
    if (meaningful) next.latest_touch = touch;
  }
  if (consented()) {
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  } else {
    _memTouch = next;  // non-persistent
  }
  return next;
}

/** Acquisition snapshot to attach to register/checkout payloads (consent-aware). */
export function getAttribution() {
  const stored = readStored();
  const t = stored.latest_touch || stored.first_touch || {};
  return {
    anon_id: getAnonId(),
    utm: t.utm || parseUTM(),
    referrer: stored.first_touch?.referrer || document.referrer || null,
    landing_page: stored.first_touch?.landing_page || window.location.pathname,
  };
}

/** Send a commercial event (fire-and-forget, consent-mode aware). */
export function trackCommercial(eventType, { props = {}, pageCategory } = {}) {
  if (!mayTrack()) return;  // explicit reject / DNT → nothing
  const anonymous = !consented();
  const stored = readStored();
  const t = stored.latest_touch || stored.first_touch || {};
  const body = {
    anon_id: getAnonId(),
    session_id: getSessionId(),
    event_type: eventType,
    page_category: pageCategory,
    page_path: window.location.pathname,
    utm: t.utm || parseUTM(),
    referrer: stored.first_touch?.referrer || document.referrer || null,
    landing_page: stored.first_touch?.landing_page || window.location.pathname,
    anonymous,        // cookieless / non-persistent ping (consent pending)
    props,
  };
  try {
    const json = JSON.stringify(body);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/api/track/event`, new Blob([json], { type: 'application/json' }));
    } else {
      fetch(`${API_URL}/api/track/event`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, keepalive: true });
    }
  } catch { /* never block UX */ }
}

/** Bind anon id to the logged-in user (called after login). Consent-gated:
 *  identity linking persists an identifier, so requires explicit consent. */
export function identify(token) {
  if (!consented() || !token) return;
  try {
    fetch(`${API_URL}/api/track/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ anon_id: getAnonId(), attribution: getAttribution() }),
    });
  } catch { /* ignore */ }
}

/** Classify a pathname into a commercial page category (or null = ignore as noise). */
export function classifyPath(pathname) {
  const p = (pathname || '').toLowerCase();
  if (p.startsWith('/library/programme/') || /^\/programmes\/[^/]+$/.test(p)) return ['programme_view', 'programme'];
  if (p.startsWith('/credential-family') || p.startsWith('/family/')) return ['credential_family_view', 'credential_family'];
  if (p.includes('ai-governance') || p === '/ai' || p.startsWith('/ai/')) return ['ai_governance_hub_view', 'ai_governance'];
  if (p.startsWith('/accreditation') || p.includes('accredit')) return ['accreditation_view', 'accreditation'];
  if (p.startsWith('/verify') || p.includes('verification')) return ['verification_view', 'verification'];
  if (p.startsWith('/trust')) return ['trust_centre_view', 'trust'];
  if (p.startsWith('/compare/')) return ['comparison_view', 'comparison'];
  return null;
}
